import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import { signalsApi } from '../api';
import { LayoutInfo } from '../types';
import {
  MAX_SIGNAL_SLOTS,
  SignalSlot,
  SignalsDefinition,
} from '../signals/types';
import {
  createDefaultSignals,
  createSignalSlot,
  createSignalStatus,
  slugifySignalsName,
  uniquifySignalSlots,
} from '../signals/createDefaultSignals';
import SignalsPreview from '../signals/components/SignalsPreview';
import SignalSlotEditor from '../signals/components/SignalSlotEditor';
import {
  parseSignalFile,
  toOperationalSignalFile,
} from '../signals/signalFile';
import { useI18n } from '../i18n';

const SignalsEditor: React.FC = () => {
  const { t } = useI18n();
  const [definition, setDefinition] = useState<SignalsDefinition>(() => createDefaultSignals());
  const [selectedSlotId, setSelectedSlotId] = useState<string | undefined>();
  const [savedName, setSavedName] = useState<string | null>(null);
  const [serverSignals, setServerSignals] = useState<LayoutInfo[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  const notify = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setNotification({ message, severity });
  };

  const refreshServerList = useCallback(async () => {
    try {
      setServerSignals(await signalsApi.list());
    } catch (err: any) {
      notify(err?.response?.data?.error || err?.message || t('signals.listFailed'), 'error');
    }
  }, [t]);

  useEffect(() => {
    refreshServerList();
  }, [refreshServerList]);

  const updateDefinition = useCallback((next: SignalsDefinition) => {
    setDefinition(next);
    setIsDirty(true);
  }, []);

  const confirmDiscard = useCallback(() => {
    if (!isDirty) return true;
    return window.confirm(t('signals.unsavedConfirm'));
  }, [isDirty, t]);

  const applyDefinition = useCallback((next: SignalsDefinition, name: string | null) => {
    setDefinition(next);
    setSelectedSlotId(next.signals[0]?.id);
    setSavedName(name);
    setIsDirty(false);
  }, []);

  const selectedSlot = definition.signals.find((s) => s.id === selectedSlotId) || definition.signals[0];
  const selectedIndex = definition.signals.findIndex((s) => s.id === selectedSlot?.id);
  const openValue = serverSignals.some((item) => item.name === savedName) ? savedName || '' : '';

  const selectSlot = (slotId: string) => {
    const signals = uniquifySignalSlots(definition.signals);
    if (signals !== definition.signals) {
      updateDefinition({ ...definition, signals });
    }
    setSelectedSlotId(slotId);
  };

  const updateSlot = (updated: SignalSlot) => {
    updateDefinition({
      ...definition,
      signals: definition.signals.map((s) => (s.id === updated.id ? updated : s)),
    });
  };

  const addSlot = () => {
    if (definition.signals.length >= MAX_SIGNAL_SLOTS) {
      notify(t('signals.maxSlots', { max: MAX_SIGNAL_SLOTS }), 'warning');
      return;
    }
    const slot = createSignalSlot(definition.signals.map((s) => s.name));
    updateDefinition({ ...definition, signals: [...definition.signals, slot] });
    setSelectedSlotId(slot.id);
  };

  const deleteSlot = (slotId: string) => {
    const next = definition.signals.filter((s) => s.id !== slotId);
    updateDefinition({ ...definition, signals: next });
    if (selectedSlotId === slotId) {
      setSelectedSlotId(next[0]?.id);
    }
  };

  const moveSlot = (from: number, to: number) => {
    if (to < 0 || to >= definition.signals.length) return;
    const next = [...definition.signals];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    updateDefinition({ ...definition, signals: next });
  };

  const handleSaveServer = async () => {
    const name = savedName || slugifySignalsName(definition.metadata.name);
    const signals = uniquifySignalSlots(definition.signals);
    const toSave = signals === definition.signals ? definition : { ...definition, signals };
    if (toSave !== definition) {
      setDefinition(toSave);
    }
    try {
      setBusy(true);
      await signalsApi.save(name, toOperationalSignalFile(toSave) as unknown as SignalsDefinition);
      setSavedName(name);
      setIsDirty(false);
      await refreshServerList();
      notify(t('signals.saved', { name: definition.metadata.name || name }), 'success');
    } catch (err: any) {
      notify(err?.response?.data?.error || err?.message || t('signals.saveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleLoadServer = async (name: string) => {
    if (!name || !confirmDiscard()) return;
    try {
      setBusy(true);
      const loaded = await signalsApi.get(name);
      const parsed = parseSignalFile(JSON.stringify(loaded));
      applyDefinition(parsed.definition, name);
      notify(t('signals.loaded', { name: parsed.definition.metadata.name || name }), 'success');
    } catch (err: any) {
      notify(err?.response?.data?.error || err?.message || t('signals.loadFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleNew = () => {
    if (!confirmDiscard()) return;
    applyDefinition(createDefaultSignals(), null);
    notify(t('signals.created'), 'info');
  };

  const handleDelete = async () => {
    if (!savedName) {
      applyDefinition(createDefaultSignals(), null);
      return;
    }
    if (!window.confirm(t('signals.deleteConfirm', { name: definition.metadata.name || savedName }))) {
      return;
    }
    try {
      setBusy(true);
      await signalsApi.delete(savedName);
      await refreshServerList();
      applyDefinition(createDefaultSignals(), null);
      notify(t('signals.deleted'), 'info');
    } catch (err: any) {
      notify(err?.response?.data?.error || err?.message || t('signals.deleteFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ height: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {isDirty && (
            <Typography variant="body2" color="warning.main" sx={{ mr: 1 }}>*</Typography>
          )}
          <TextField
            size="small"
            label={t('signals.name')}
            value={definition.metadata.name}
            onChange={(e) => updateDefinition({
              ...definition,
              metadata: { ...definition.metadata, name: e.target.value },
            })}
            sx={{ width: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('signals.openSaved')}</InputLabel>
            <Select
              label={t('signals.openSaved')}
              value={openValue}
              onChange={(e) => handleLoadServer(String(e.target.value))}
            >
              <MenuItem value="">
                <em>{t('signals.openSaved')}</em>
              </MenuItem>
              {serverSignals.map((item) => (
                <MenuItem key={item.name} value={item.name}>
                  {item.title || item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider orientation="vertical" flexItem />
          <Button size="small" startIcon={<Add />} onClick={handleNew} disabled={busy}>
            {t('common.newItem')}
          </Button>
          <Button size="small" variant="contained" startIcon={<Save />} disabled={busy} onClick={handleSaveServer}>
            {t('common.save')}
          </Button>
          <Button size="small" color="error" startIcon={<Delete />} disabled={busy} onClick={handleDelete}>
            {t('common.delete')}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {t('signals.hint')}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
        <Box
          sx={{
            width: 280,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            minHeight: 0,
            gap: 1,
            py: 1.5,
            px: 1.5,
            bgcolor: 'action.hover',
            borderRight: 1,
            borderColor: 'divider',
          }}
        >
          {definition.signals.length === 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {t('signals.emptySlots')}
            </Typography>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
              <SignalsPreview
                signals={definition.signals}
                selectedSlotId={selectedSlot?.id}
                onSelectSlot={selectSlot}
                size="normal"
                fill
                showLabels
                labelMode="slot"
              />
            </Box>
          )}
          <Button
            size="small"
            startIcon={<Add />}
            disabled={definition.signals.length >= MAX_SIGNAL_SLOTS}
            onClick={addSlot}
            sx={{ flexShrink: 0, alignSelf: 'center' }}
          >
            {t('signals.addSlot')}
          </Button>
        </Box>

        <Paper sx={{ flex: 1, borderRadius: 0, overflow: 'auto', p: 2 }}>
          {selectedSlot ? (
            <SignalSlotEditor
              slot={selectedSlot}
              siblingNames={definition.signals.filter((s) => s.id !== selectedSlot.id).map((s) => s.name)}
              onSlotChange={updateSlot}
              onAddStatus={() => {
                const status = createSignalStatus(selectedSlot.id, selectedSlot.statuses.length);
                updateSlot({ ...selectedSlot, statuses: [...selectedSlot.statuses, status] });
              }}
              onDeleteStatus={(statusId) => {
                const statuses = selectedSlot.statuses.filter((s) => s.id !== statusId);
                const previewStatus = selectedSlot.previewStatus === statusId
                  ? statuses[0]?.id
                  : selectedSlot.previewStatus;
                updateSlot({ ...selectedSlot, statuses, previewStatus });
              }}
              onMoveUp={selectedIndex > 0 ? () => moveSlot(selectedIndex, selectedIndex - 1) : undefined}
              onMoveDown={selectedIndex < definition.signals.length - 1 ? () => moveSlot(selectedIndex, selectedIndex + 1) : undefined}
              onDeleteSlot={() => {
                if (window.confirm(t('signals.deleteSlotConfirm', { name: selectedSlot.name }))) {
                  deleteSlot(selectedSlot.id);
                }
              }}
            />
          ) : (
            <Typography color="text.secondary">{t('signals.selectSlot')}</Typography>
          )}
        </Paper>
      </Box>

      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notification ? (
          <Alert severity={notification.severity} onClose={() => setNotification(null)}>
            {notification.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default SignalsEditor;
